import { stopIgnoring } from 'owa-locstrings/lib/strings/stopignoring.locstring.json';
import { ignore } from 'owa-locstrings/lib/strings/ignore.locstring.json';
import { unpin } from 'owa-locstrings/lib/strings/unpin.locstring.json';
import { snooze } from 'owa-locstrings/lib/strings/snooze.locstring.json';
import { categoryMenuItemMenuText } from 'owa-locstrings/lib/strings/categorymenuitemmenutext.locstring.json';
import {
    copy,
    openInNewTab,
    findEmailFromSender,
} from './getFilterableMailListContextMenuItems.locstring.json';
import { unflag } from 'owa-locstrings/lib/strings/unflag.locstring.json';
import { block } from 'owa-locstrings/lib/strings/block.locstring.json';
import { move } from 'owa-locstrings/lib/strings/move.locstring.json';
import { restoreItem } from 'owa-locstrings/lib/strings/restoreitem.locstring.json';
import { markAsNotJunk, markAsJunk } from '../strings.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { flag } from 'owa-locstrings/lib/strings/flag.locstring.json';
import { pin } from 'owa-locstrings/lib/strings/pin.locstring.json';
import { archive } from 'owa-locstrings/lib/strings/archive.locstring.json';
import { deleteItem } from 'owa-locstrings/lib/strings/deleteitem.locstring.json';
import { showInImmersiveReader as showInImmersiveReader_1 } from 'owa-locstrings/lib/strings/showinimmersivereader.locstring.json';
import { itemHeaderTranslate } from 'owa-locstrings/lib/strings/itemheadertranslate.locstring.json';
import { createRule } from 'owa-locstrings/lib/strings/createrule.locstring.json';
import { assignPolicyMenuItemName } from 'owa-locstrings/lib/strings/assignpolicymenuitemname.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { reportAbuse } from 'owa-locstrings/lib/strings/reportabuse.locstring.json';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import loc from 'owa-localize';
import { OwaDate } from 'owa-datetime';
import { FilterableMenuItem, ShouldShowBehavior } from 'owa-filterable-menu';
import { showInImmersiveReader } from 'owa-immersive-reader-store';
import { getCategoryMenuPropertiesForContextMenu } from 'owa-mail-category-view';
import createScheduleSubMenu from 'owa-mail-datepicker/lib/utils/createScheduleSubMenu';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyMarkAsReadInTable } from 'owa-mail-mark-read-actions';
import { getCopyToProperties, getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';
import { lazyMoveReadingPaneToTab } from 'owa-mail-reading-pane-store';
import { isReadingPanePositionBottom } from 'owa-mail-layout';
import { lazyOnReportAbuseOptionSelected } from 'owa-mail-report-abuse';
import { getAssignPolicyPropertiesForMailContextMenu } from 'owa-mail-retention-policies';
import { lazyShowInboxRuleDialog } from 'owa-mail-rule-view';
import * as lazyTriageActions from 'owa-mail-triage-action';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

import {
    listViewStore,
    MailRowDataPropertyGetter,
    TableView,
    TableQueryType,
    shouldSuppressServerMarkReadOnReplyOrForward,
} from 'owa-mail-list-store';
import {
    getMailMenuItemShouldShowMap,
    getMailMenuItemShouldDisable,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';
import {
    lazyForwardConversation,
    lazyReplyToConversation,
    lazyForwardItem,
    lazyReplyToItem,
    lazyShowConversationInImmersiveReader,
} from 'owa-mail-message-actions';
import { lazyScheduleRows } from 'owa-mail-triage-action';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import { onFindEmailFromSenderClicked as onFindEmailFromSenderClickedAction } from 'owa-mail-search-next-lazy';

const contextMenuActionSource = 'ContextMenu';

// Used as a key for divider, because IContextualMenuItem requires each element to have a different key
let dividerKey = 0;

/**
 * Get an array of filterable menu items for mail list context menu
 * @param rowKeys the row keys to show the context menu
 * @param tableViewId the id of the table that contains these rows
 * @return the context menu items
 */
export default function getFilterableMailListContextMenuItems(
    rowKeys: string[],
    tableViewId: string,
    onDismiss: (ev?: any) => void
): FilterableMenuItem[] {
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();

    return [
        // Reply/Reply all/Forward
        new FilterableMenuItem(
            loc(itemHeaderReply),
            null /* icon */,
            () => onReplyRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.Reply]
        ),
        new FilterableMenuItem(
            loc(itemHeaderReplyAll),
            null /* icon */,
            () => onReplyAllRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReplyAllForward]
        ),
        new FilterableMenuItem(
            loc(itemHeaderForward),
            null /* icon */,
            () => onForwardRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReplyAllForward]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.Move]() ||
                shouldShowBehaviorMap[MenuItemType.Copy]() ||
                shouldShowBehaviorMap[MenuItemType.Delete]() ||
                shouldShowBehaviorMap[MenuItemType.Restore]() ||
                shouldShowBehaviorMap[MenuItemType.Archive]() ||
                shouldShowBehaviorMap[MenuItemType.EmptyFolder]() ||
                shouldShowBehaviorMap[MenuItemType.Categories]()
            );
        }),

        // Delete/Restore/Archive
        new FilterableMenuItem(
            loc(deleteItem),
            null /* icon */,
            () => onDeleteClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Delete],
            null /* display */,
            getMailMenuItemShouldDisable(MenuItemType.Delete)
        ),
        new FilterableMenuItem(
            loc(restoreItem),
            null /* icon */,
            () =>
                lazyTriageActions.lazyRestoreMailListRowsAction.importAndExecute(
                    rowKeys,
                    tableViewId
                ),
            shouldShowBehaviorMap[MenuItemType.Restore]
        ),
        new FilterableMenuItem(
            loc(archive),
            null /* icon */,
            () => onArchiveClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Archive]
        ),
        new FilterableMenuItem(
            loc(move),
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Move],
            null /* displayOption*/,
            false /* disabled */,
            getMoveToPropertiesForContextMenu(
                tableViewId,
                onDismiss,
                contextMenuActionSource,
                shouldShowBehaviorMap,
                DirectionalHint.rightTopEdge
            )
        ),
        new FilterableMenuItem(
            loc(copy),
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Copy],
            null /* displayOption*/,
            false /* disabled */,
            getCopyToProperties(tableViewId, onDismiss, contextMenuActionSource)
        ),
        new FilterableMenuItem(
            loc(categoryMenuItemMenuText),
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Categories],
            null /* displayOption*/,
            false /* disabled */,
            getCategoryMenuPropertiesForContextMenu(tableViewId, onDismiss, 'ContextLV')
        ),

        // Empty folder
        new FilterableMenuItem(
            loc(deleteItem),
            null /* icon */,
            () => {
                const tableView = listViewStore.tableViews.get(tableViewId);

                lazyTriageActions.lazyEmptyTableView.importAndExecute(
                    tableView,
                    false /*isExplicitSoftDelete*/,
                    'ContextLV',
                    tableView.virutalSelectAllExclusionList
                );
            },
            shouldShowBehaviorMap[MenuItemType.EmptyFolder]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsRead]() ||
                shouldShowBehaviorMap[MenuItemType.MarkAsUnread]() ||
                shouldShowBehaviorMap[MenuItemType.Flag]() ||
                shouldShowBehaviorMap[MenuItemType.Unflag]() ||
                shouldShowBehaviorMap[MenuItemType.Pin]() ||
                shouldShowBehaviorMap[MenuItemType.Unpin]()
            );
        }),

        // Mark as read/unread
        new FilterableMenuItem(
            loc(markAsRead),
            null /* icon */,
            () => onMarkAsReadUnreadClicked(tableViewId, true /* isReadValueSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsRead]
        ),
        new FilterableMenuItem(
            loc(markAsUnread),
            null /* icon */,
            () => onMarkAsReadUnreadClicked(tableViewId, false /* isReadValueSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsUnread]
        ),

        // Schedule/Unschedule
        new FilterableMenuItem(
            loc(snooze),
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Schedule],
            null /* displayOption*/,
            false /* disabled */,
            createScheduleSubMenu(tableViewId, onScheduleItemClicked)
        ),

        // Flag/Unflag
        new FilterableMenuItem(
            loc(flag),
            null /* icon */,
            () => markRowsAsFlagged(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Flag]
        ),
        new FilterableMenuItem(
            loc(unflag),
            null /* icon */,
            () => markRowsAsNotFlagged(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Unflag]
        ),

        // Pin/unpin
        new FilterableMenuItem(
            loc(pin),
            null /* icon */,
            () => markRowsPinnedUnpinned(rowKeys, tableViewId, true /* shouldMarkAsPinned */),
            shouldShowBehaviorMap[MenuItemType.Pin]
        ),
        new FilterableMenuItem(
            loc(unpin),
            null /* icon */,
            () => markRowsPinnedUnpinned(rowKeys, tableViewId, false /* shouldMarkAsPinned */),
            shouldShowBehaviorMap[MenuItemType.Unpin]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsJunk]() ||
                shouldShowBehaviorMap[MenuItemType.MarkAsNotJunk]() ||
                shouldShowBehaviorMap[MenuItemType.Ignore]() ||
                shouldShowBehaviorMap[MenuItemType.StopIgnoring]() ||
                shouldShowBehaviorMap[MenuItemType.Block]()
            );
        }),

        // Junk/Not junk/Block
        new FilterableMenuItem(
            loc(markAsJunk),
            null /* icon */,
            () => onJunkNotJunkClicked(rowKeys, tableViewId, true /* isJunkValueToSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsJunk]
        ),
        new FilterableMenuItem(
            loc(markAsNotJunk),
            null /* icon */,
            () => onJunkNotJunkClicked(rowKeys, tableViewId, false /* isJunkValueToSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsNotJunk]
        ),
        new FilterableMenuItem(
            loc(ignore),
            null /* icon */,
            () => onIgnoreStopIgnoreClicked(rowKeys, tableViewId, true),
            shouldShowBehaviorMap[MenuItemType.Ignore]
        ),
        new FilterableMenuItem(
            loc(reportAbuse),
            null /* icon */,
            () => onReportAbuseClicked(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReportAbuse]
        ),
        new FilterableMenuItem(
            loc(stopIgnoring),
            null /* icon */,
            () => onIgnoreStopIgnoreClicked(rowKeys, tableViewId, false),
            shouldShowBehaviorMap[MenuItemType.StopIgnoring]
        ),
        new FilterableMenuItem(
            loc(block),
            null /* icon */,
            () => onBlockClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Block]
        ),

        newDivider(() => {
            return shouldShowBehaviorMap[MenuItemType.FindEmailFromSender]();
        }),

        new FilterableMenuItem(
            loc(findEmailFromSender),
            null /* icon */,
            () => onFindEmailFromSenderClicked(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.FindEmailFromSender]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.InboxRule]() ||
                shouldShowBehaviorMap[MenuItemType.Translate]() ||
                shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]()
            );
        }),

        new FilterableMenuItem(
            loc(createRule),
            null, // no - icon
            () => onCreateRuleClicked(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.InboxRule],
            null /* displayOption */
        ),

        // Translate
        new FilterableMenuItem(
            loc(itemHeaderTranslate),
            null, // no - icon
            () => onTranslateClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Translate]
        ),

        // Show in immersive reader
        new FilterableMenuItem(
            loc(showInImmersiveReader_1),
            null, // no - icon
            () => {
                onShowInImmersiveReaderClicked(rowKeys[0], tableViewId);
            },
            shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]
        ),

        new FilterableMenuItem(
            loc(openInNewTab),
            null, // no - icon
            () => onOpenInNewTabClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.OpenInNewTab]
        ),

        newDivider(() => {
            return shouldShowBehaviorMap[MenuItemType.AssignPolicy]();
        }),

        // Assign Policy
        new FilterableMenuItem(
            loc(assignPolicyMenuItemName),
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.AssignPolicy],
            null /* displayOption*/,
            false /* disabled */,
            getAssignPolicyPropertiesForMailContextMenu(tableViewId, 'ContextLV')
        ),
    ];
}

/**
 * Called when user clicks on schedule
 * @param tableView the table view
 * @param date date to schedule to
 */
async function onScheduleItemClicked(tableView: TableView, date?: OwaDate) {
    const rowKeys = [...tableView.selectedRowKeys.keys()];
    const scheduleRows = await lazyScheduleRows.import();
    scheduleRows(rowKeys, tableView.id, contextMenuActionSource /* actionSource */, date);
}

/**
 * Called when user clicks on the reply menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onReplyRow(rowKey: string, tableViewId: string) {
    onReplyReplyAllInternal(false /* isReplyAll */, rowKey, tableViewId);
}

/**
 * Called when user clicks on the reply all menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onReplyAllRow(rowKey: string, tableViewId: string) {
    onReplyReplyAllInternal(true /* isReplyAll */, rowKey, tableViewId);
}

let onReplyReplyAllInternal = (isReplyAll: boolean, rowKey: string, tableViewId: string) => {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
        tableView
    );
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
    let instrumentationContexts;
    if (tableView.tableQuery.type == TableQueryType.Search) {
        instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    }
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyReplyToConversation.importAndExecute(
                rowId,
                isReplyAll,
                'ContextMenu',
                instrumentationContexts,
                isReadingPanePositionBottom() /*isFullCompose*/,
                suppressServerMarkReadOnReplyOrForward
            );
            break;

        case ReactListViewType.Message:
            lazyReplyToItem.importAndExecute(
                rowId,
                isReplyAll,
                instrumentationContexts,
                'ContextMenu',
                suppressServerMarkReadOnReplyOrForward
            );
    }
};

/**
 * Called when user clicks on the forward menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onForwardRow(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
    const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
        tableView
    );
    let instrumentationContexts;
    if (tableView.tableQuery.type == TableQueryType.Search) {
        instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    }
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyForwardConversation.importAndExecute(
                rowId,
                'ContextMenu',
                instrumentationContexts,
                suppressServerMarkReadOnReplyOrForward
            );
            break;
        case ReactListViewType.Message:
            lazyForwardItem.importAndExecute(
                rowId,
                'ContextMenu',
                instrumentationContexts,
                suppressServerMarkReadOnReplyOrForward
            );
            break;
    }
}

/**
 * Create a divider which is used in the mail list context menu
 * @param shouldShow the should show behavior which indicates whether to show the context menu or not
 */
function newDivider(shouldShowBehavior?: ShouldShowBehavior): FilterableMenuItem {
    return new FilterableMenuItem(
        '-' /* name */,
        null /* icon */,
        null /* onClick */,
        shouldShowBehavior,
        null /* displayOption */,
        null /* disabled*/,
        null /* menuContent */,
        (dividerKey++).toString()
    );
}

/**
 * Called when user clicks on the delete menu item
 */
function onDeleteClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    if (tableView.isInVirtualSelectAllMode) {
        // When in virtual selection, i.e non search and select all mode,
        // delete all in the folder
        lazyTriageActions.lazyEmptyTableView.importAndExecute(
            tableView,
            false /*isExplicitSoftDelete*/,
            'ContextLV',
            tableView.virutalSelectAllExclusionList
        );
    } else {
        lazyTriageActions.lazyDeleteMailListRows.importAndExecute(
            rowKeys,
            tableViewId,
            false /*isExplicitSoftDelete*/,
            contextMenuActionSource
        );
    }
}

/**
 * Called when user clicks on the archive menu item
 */
function onArchiveClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyArchiveMailListRows.importAndExecute(
        rowKeys,
        tableView,
        contextMenuActionSource
    );
}

/**
 * Called when the user clicks on the "Show in immersive reader" menu item
 */
function onShowInImmersiveReaderClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);

    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyShowConversationInImmersiveReader.importAndExecute(
                rowId /* conversationId */,
                contextMenuActionSource
            );
            break;

        case ReactListViewType.Message:
            showInImmersiveReader(rowId /* itemId */, contextMenuActionSource);
            break;
    }
}

/**
 * Called when the user clicks on the "Open in new tab" menu item
 */
function onOpenInNewTabClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
    for (let i = 0; i < rowKeys.length; i++) {
        const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKeys[i], tableView);
        const rowSubject = MailRowDataPropertyGetter.getSubject(rowKeys[i], tableView);
        const rowCategories = MailRowDataPropertyGetter.getCategories(rowKeys[i], tableView);
        lazyMoveReadingPaneToTab.importAndExecute(
            rowId,
            rowSubject,
            rowCategories,
            true /*makeActive*/,
            instrumentationContexts[i]
        );
    }
}

function onMarkAsReadUnreadClicked(tableViewId: string, isReadValueSet: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);

    lazyMarkAsReadInTable.importAndExecute(
        contextMenuActionSource,
        tableView.virutalSelectAllExclusionList /* exclusionList */,
        tableView.isInVirtualSelectAllMode /* isActingOnAllItemsInTable */,
        isReadValueSet,
        [...tableView.selectedRowKeys.keys()],
        tableView
    );
}

function onFindEmailFromSenderClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const lastSenderMailbox = MailRowDataPropertyGetter.getLastSenderMailbox(rowKey, tableView);
    onFindEmailFromSenderClickedAction(lastSenderMailbox.Name, lastSenderMailbox.EmailAddress);
}

function onBlockClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyBlockLastSendersFromTable.importAndExecute(
        rowKeys,
        tableView,
        'ContextLV'
    );
}

function markRowsAsFlagged(rowKeys: string[], tableViewId: string) {
    lazyTriageActions.lazySetMailListRowsFlagState.importAndExecute(
        rowKeys,
        tableViewId,
        { FlagStatus: 'Flagged' },
        contextMenuActionSource
    );
}

function markRowsAsNotFlagged(rowKeys: string[], tableViewId: string) {
    lazyTriageActions.lazySetMailListRowsFlagState.importAndExecute(
        rowKeys,
        tableViewId,
        { FlagStatus: 'NotFlagged' },
        contextMenuActionSource
    );
}

function onJunkNotJunkClicked(rowKeys: string[], tableViewId: string, isJunkValueToSet: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyMarkRowsJunkNotJunk.importAndExecute(
        rowKeys,
        tableView,
        isJunkValueToSet,
        contextMenuActionSource /* actionSource */
    );
}

async function onIgnoreStopIgnoreClicked(
    rowKeys: string[],
    tableViewId: string,
    shouldIgnore: boolean
) {
    const toggleIgnoreConversations = await lazyTriageActions.lazyToggleIgnoreConversations.import();
    toggleIgnoreConversations(rowKeys, tableViewId, shouldIgnore, 'ContextLV');
}

function markRowsPinnedUnpinned(
    rowKeys: string[],
    tableViewId: string,
    shouldMarkAsPinned: boolean
) {
    lazyTriageActions.lazyMarkRowsPinnedUnpinned.importAndExecute(
        rowKeys,
        tableViewId,
        shouldMarkAsPinned,
        contextMenuActionSource
    );
}

/**
 * Called when user clicks on the create rule menu item
 */
function onCreateRuleClicked(rowKey: string, tableViewId: string) {
    // Get the sender from the single selected conversation.
    // Ensuring a single selection and that the selection
    // has an SMTP address is handled in the context menu should show behavior map
    const sender = MailRowDataPropertyGetter.getLastSenderMailbox(
        rowKey,
        listViewStore.tableViews.get(tableViewId)
    );
    lazyShowInboxRuleDialog.importAndExecute(sender, 'ContextLV');
}

/**
 * Called when the user clicks on the "Translate" menu item
 */
function onTranslateClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyTranslateFromTable.importAndExecute(rowKeys, tableView, 'ContextLV');
}

/**
 * Called when the user clicks on the "Report Abuse" menu item
 */
function onReportAbuseClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    lazyOnReportAbuseOptionSelected.importAndExecute(
        rowKey,
        tableView,
        instrumentationContexts,
        contextMenuActionSource
    );
}
